import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateField } from '@/components/ui/date-time-field';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { EmployeePicker } from '@/components/registration';
import { mfgApi } from './mfgApi';
import { todayJst } from '@/utils/jstDate';

const today = () => todayJst();

export default function CorrectiveDialog({ open, equipmentId, equipmentList, onClose, onDone }) {
  const { t } = useT();
  const [eqId, setEqId] = useState('');
  const [form, setForm] = useState({ reported_on: today(), performed_on: today(), symptom: '', action: '', parts: '', downtime_hours: '', cost: '' });
  const [status, setStatus] = useState('open');
  const [setBreakdown, setSetBreakdown] = useState(true);
  const [busy, setBusy] = useState(false);
  // 2026-10-23 · 担当は社員マスターから選ぶ（自由入力をやめる）
  const [employees, setEmployees] = useState([]);
  const [owner, setOwner] = useState('');

  useEffect(() => {
    if (!open) return;
    setEqId(equipmentId || '');
    setForm({ reported_on: today(), performed_on: today(), symptom: '', action: '', parts: '', downtime_hours: '', cost: '' });
    setStatus('open'); setSetBreakdown(true); setOwner('');
    mfgApi.employees().then((list) => {
      const rows = Array.isArray(list) ? list : (list?.items || []);
      setEmployees(rows.filter((e) => (e.status || 'active') === 'active')
        .map((e) => ({ id: e.id, name: e.name || e.code || e.email || e.id })));
    }).catch(() => setEmployees([]));
  }, [open, equipmentId]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    if (!eqId) { toast.error(t('mfg.maint.equipment')); return; }
    setBusy(true);
    try {
      await mfgApi.createCorrective({
        equipment_id: eqId, ...form,
        downtime_hours: form.downtime_hours === '' ? null : Number(form.downtime_hours),
        cost: form.cost === '' ? null : Number(form.cost),
        performed_by: owner || null,
        performed_by_employee_id: employees.find((e) => e.name === owner)?.id || null,
        status, set_breakdown: setBreakdown,
      });
      toast.success(t('mfg.common.saved')); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="corrective-dialog">
        <DialogHeader><DialogTitle>{t('mfg.maint.corrTitle')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!equipmentId && (
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.equipment')}</Label>
              <Select value={eqId} onValueChange={setEqId}>
                <SelectTrigger data-testid="corrective-equipment-select" className="h-10 mt-1"><SelectValue placeholder={t('mfg.maint.equipment')} /></SelectTrigger>
                <SelectContent>{(equipmentList || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.asset_no} — {e.name}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.reportedOn')}</Label><DateField value={form.reported_on || ''} onChange={set('reported_on')} className="mt-1" inputClassName="h-10" /></div>
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.performedOn')}</Label><DateField value={form.performed_on || ''} onChange={set('performed_on')} className="mt-1" inputClassName="h-10" /></div>
          </div>
          <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.symptom')}</Label><Input data-testid="corrective-symptom-input" value={form.symptom} onChange={set('symptom')} className="h-10 mt-1" /></div>
          <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.action')}</Label><Input value={form.action} onChange={set('action')} className="h-10 mt-1" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.parts')}</Label><Input value={form.parts} onChange={set('parts')} className="h-10 mt-1" /></div>
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.downtime')}</Label><Input type="number" value={form.downtime_hours} onChange={set('downtime_hours')} className="h-10 mt-1" /></div>
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.cost')}</Label><Input type="number" value={form.cost} onChange={set('cost')} className="h-10 mt-1" /></div>
          </div>
          <div><Label className="text-xs font-semibold text-zinc-600">担当</Label>
            <EmployeePicker value={owner} employees={employees} onChange={setOwner}
              testId="corrective-owner" /></div>
          <div className="flex items-center gap-2"><Switch checked={setBreakdown} onCheckedChange={setSetBreakdown} data-testid="corrective-breakdown-switch" /><span className="text-sm text-zinc-600">{t('mfg.maint.setBreakdown')}</span></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('mfg.common.cancel')}</Button>
          <Button data-testid="corrective-save-btn" onClick={save} disabled={busy} className="bg-teal-700 hover:bg-teal-800">{busy ? t('mfg.common.saving') : t('mfg.common.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
